import { useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { Button as PolarisButton, Modal as PolarisModal, Page as PolarisPage, PageActions, Select as PolarisSelect, TextField as PolarisTextField } from '@shopify/polaris';
import { missingRequired, requiredMessage, validateFromButton, validateRequiredFields } from '../../../../packages/shared/form-feedback/validation';

export function TextField({ requiredIndicator, error, onBlur, onChange, ...props }: ComponentProps<typeof PolarisTextField>) {
  const [touched, setTouched] = useState(false);
  const [nativeError, setNativeError] = useState({ value: '', message: '' });
  const nativeMessage = !props.disabled && nativeError.value === String(props.value ?? '') ? nativeError.message : '';
  const missing = requiredIndicator && !props.disabled && missingRequired(props.value);
  return <div onInvalidCapture={event => {
    event.preventDefault();
    setTouched(true);
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      setNativeError({ value: target.value, message: target.validationMessage });
    }
  }}>
    <PolarisTextField {...props} requiredIndicator={requiredIndicator} error={error || nativeMessage || (touched && missing ? requiredMessage : undefined)}
      onChange={(value, id) => { setNativeError({ value: '', message: '' }); onChange?.(value, id); }}
      onBlur={event => { setTouched(true); onBlur?.(event); }} />
  </div>;
}

export function Select({ requiredIndicator, error, onBlur, ...props }: ComponentProps<typeof PolarisSelect>) {
  const [touched, setTouched] = useState(false);
  const missing = requiredIndicator && !props.disabled && missingRequired(props.value);
  return <div onInvalidCapture={event => { event.preventDefault(); setTouched(true); }}>
    <PolarisSelect {...props} requiredIndicator={requiredIndicator} error={error || (touched && missing ? requiredMessage : undefined)}
      onBlur={event => { setTouched(true); onBlur?.(event); }} />
  </div>;
}

export function Button({ validate, ...props }: ComponentProps<typeof PolarisButton> & { validate?: boolean }) {
  if (!validate) return <PolarisButton {...props} />;
  return <span style={{ display: 'contents' }} onClickCapture={event => {
    if (validate && !validateFromButton(event.currentTarget)) { event.preventDefault(); event.stopPropagation(); }
  }}><PolarisButton {...props} /></span>;
}

export function FormScope({ children }: { children: ReactNode }) {
  return <div data-form-scope="">{children}</div>;
}

export function Form({ onSubmit, onInvalidCapture, children, ...props }: ComponentProps<'form'>) {
  return <form {...props} onInvalidCapture={event => {
    event.currentTarget.querySelector<HTMLElement>(':invalid')?.focus();
    onInvalidCapture?.(event);
  }} onSubmit={event => {
    if (!validateRequiredFields(event.currentTarget)) { event.preventDefault(); return; }
    onSubmit?.(event);
  }}>{children}</form>;
}

function FeedbackModal({ primaryAction, children, ...props }: ComponentProps<typeof PolarisModal>) {
  const scope = useRef<HTMLDivElement>(null);
  return <PolarisModal {...props} primaryAction={primaryAction && { ...primaryAction, onAction: () => {
    if (validateRequiredFields(scope.current)) primaryAction.onAction?.();
  } }}><div ref={scope} data-form-scope="">{children}</div></PolarisModal>;
}
export const Modal = Object.assign(FeedbackModal, { Section: PolarisModal.Section });

/** A choice must be selected, not merely typed into its search box. */
export function RequiredChoice({ label, value, disabled }: { label: string; value: string; disabled: boolean }) {
  return <TextField label={label} value={value} onChange={() => {}} autoComplete="off" requiredIndicator disabled={disabled}
    readOnly helpText="Choose an item using the search below." />;
}

type PageProps = ComponentProps<typeof PolarisPage> & { primaryActionPosition?: 'header' | 'footer' };

export function Page({ primaryAction, primaryActionPosition = 'header', children, ...props }: PageProps) {
  const scope = useRef<HTMLDivElement>(null);
  const action = primaryAction && typeof primaryAction === 'object' && 'content' in primaryAction
    ? { ...primaryAction, onAction: () => { if (validateRequiredFields(scope.current)) primaryAction.onAction?.(); } }
    : primaryAction;
  return <div ref={scope} data-form-scope=""><PolarisPage {...props} primaryAction={primaryActionPosition === 'header' ? action : undefined}>
    {children}
    {primaryActionPosition === 'footer' && action && <PageActions primaryAction={action} />}
  </PolarisPage></div>;
}
